/**
 * Façade de persistance des factures — branchée sur l'API.
 *
 * Tout l'écran passe par ici et ne connaît que des promesses. Les endpoints
 * sont ceux décrits dans PROMPT_API_FACTURATION.md et implémentés par
 * `Comptabilite\FactureController`.
 *
 * Les URL sont construites à partir de `Comptabilite.facture.index`, la seule
 * route de facturation exposée par `getAllRoutes` : les routes nommées
 * ajoutées depuis (parametres, envoyer, envois) n'y figurent pas, s'y fier
 * casserait au premier déploiement.
 */

import Vue from 'vue'
import {definirFormatNumerotation} from './numerotation'
import magasin from '../../vuex/store'
import {normaliser} from './modele'

function base () {
  return magasin.getters.getRoute('Comptabilite.facture.index')
}

/**
 * Une ligne de l'API devient une facture du frontend : le document vit dans
 * `donnees`, l'identifiant et le statut restent côté colonne.
 */
function depuisApi (ligne) {
  const facture = normaliser(ligne.donnees || {})
  facture.idServeur = ligne.id
  facture.statut = ligne.statut || null
  facture.envois = ligne.envois || []
  return facture
}

/**
 * Une facture du frontend devient une entrée du lot à créer. `origine` est
 * recopiée en colonnes pour que le serveur puisse filtrer et relier
 * l'inscription.
 */
/**
 * Le JSON conservé est la facture telle que l'écran la manipule — pas les
 * champs que depuisApi() y a greffés. `envois` surtout : il grossit à chaque
 * envoi et n'a rien à faire dans le document archivé.
 */
function sansChampsServeur (facture) {
  const copie = Object.assign({}, facture)
  delete copie.idServeur
  delete copie.statut
  delete copie.envois
  return copie
}

function versApi (facture, pdfBase64) {
  const origine = facture.origine || {}
  const entree = {
    donnees: sansChampsServeur(facture),
    origine_type: origine.type || null,
    origine_id: origine.idFormation || origine.id || null,
    id_inscription: origine.idInscription || null
  }
  if (pdfBase64) entree.pdf_base64 = pdfBase64
  return entree
}

export function depotDisponible () {
  return !!base()
}

/** Les factures ne vivent plus dans le navigateur. */
export function depotLocal () {
  return false
}

export function listerFactures (filtre) {
  const criteres = filtre || {}
  const parametres = {}
  if (criteres.origineType) parametres.origine_type = criteres.origineType
  if (criteres.origineId !== undefined && criteres.origineId !== null) {
    parametres.origine_id = criteres.origineId
  }
  if (criteres.statut) parametres.statut = criteres.statut
  if (criteres.idMembre) parametres.id_membre = criteres.idMembre

  return Vue.http.get(base(), {params: parametres}).then(function (reponse) {
    return ((reponse.data || {}).factures || []).map(depuisApi)
  })
}

/**
 * Enregistre un lot en un seul appel. Renvoie les factures telles que le
 * serveur les a retenues : c'est lui qui tranche la numérotation, l'appelant
 * doit reprendre ce qu'il reçoit plutôt que ce qu'il a envoyé.
 *
 * `pdfParFacture` associe l'identifiant local d'une facture à son PDF en
 * base64. Sans PDF, la facture est stockée mais ne pourra pas être envoyée.
 */
export function enregistrerFactures (factures, pdfParFacture) {
  const pdfs = pdfParFacture || {}
  const lot = (factures || []).map(function (facture) {
    return versApi(facture, pdfs[facture.id])
  })
  if (!lot.length) return Promise.resolve([])

  return Vue.http.post(base(), {factures: lot}).then(function (reponse) {
    return ((reponse.data || {}).factures || []).map(depuisApi)
  })
}

export function majFacture (facture, pdfBase64) {
  if (!facture.idServeur) return Promise.reject(new Error('Facture jamais enregistrée'))
  const corps = {donnees: sansChampsServeur(facture)}
  if (facture.statut) corps.statut = facture.statut
  if (pdfBase64) corps.pdf_base64 = pdfBase64

  return Vue.http.put(base() + '/' + facture.idServeur, corps).then(function (reponse) {
    return depuisApi(reponse.data || {})
  })
}

export function supprimerFacture (idServeur) {
  return Vue.http.delete(base() + '/' + idServeur).then(function () {
    return idServeur
  })
}

/**
 * Envoi en masse. Le serveur répond en 202 avec un résultat par facture :
 * l'envoi part en file, il ne faut pas attendre qu'il aboutisse.
 */
export function envoyerFactures (parametres) {
  const options = parametres || {}
  return Vue.http.post(base() + '/envoyer', {
    ids: options.ids || [],
    sujet: options.sujet || '',
    message: options.message || '',
    copie: options.copie || null
  }).then(function (reponse) {
    return (reponse.data || {}).resultats || []
  })
}

export function lireEnvois (idServeur) {
  return Vue.http.get(base() + '/' + idServeur + '/envois').then(function (reponse) {
    return (reponse.data || {}).envois || []
  })
}

/** Émetteur, coordonnées de paiement, mentions et tarifs, servis par l'API. */
export function lireParametres () {
  return Vue.http.get(base() + '/parametres').then(function (reponse) {
    return reponse.data || {}
  })
}

/**
 * Corrige le numéro d'une facture déjà enregistrée.
 *
 * Le serveur refuse dès qu'un envoi existe (409) : la communication structurée
 * dérive du numéro, et le destinataire paierait avec l'ancienne. Le document
 * part dans le même appel, pour qu'il n'existe aucun instant où la colonne et
 * le PDF conservé se contredisent.
 */
export function renumeroterFacture (facture, numero, pdfBase64) {
  if (!facture.idServeur) return Promise.reject(new Error('Facture jamais enregistrée'))
  const corps = {numero: numero, donnees: Object.assign(sansChampsServeur(facture), {numero: numero})}
  if (pdfBase64) corps.pdf_base64 = pdfBase64

  return Vue.http.put(base() + '/' + facture.idServeur + '/numero', corps)
    .then(function (reponse) { return depuisApi(reponse.data || {}) })
}

/**
 * Renumérotation d'un lot, tout ou rien : un lot à moitié renuméroté serait
 * pire que pas de renumérotation du tout. Un refus rend un 422 dont le corps
 * porte `refus`, une ligne par facture fautive.
 */
export function renumeroterLot (entrees) {
  const lot = (entrees || []).map(function (entree) {
    const ligne = {
      id: entree.facture.idServeur,
      numero: entree.numero,
      donnees: Object.assign(sansChampsServeur(entree.facture), {numero: entree.numero})
    }
    if (entree.pdf) ligne.pdf_base64 = entree.pdf
    return ligne
  })
  if (!lot.length) return Promise.resolve([])

  return Vue.http.put(base() + '/numeros', {factures: lot}).then(function (reponse) {
    return ((reponse.data || {}).factures || []).map(depuisApi)
  })
}

/**
 * Pose le format de numérotation servi par l'API.
 *
 * Sans cet appel, le frontend proposerait et validerait des numéros « FACT »
 * alors que le serveur a changé de préfixe : tous les numéros du lot seraient
 * signalés hors format, puis renumérotés en silence à l'enregistrement.
 * Un échec est sans gravité — on reste sur le format historique.
 *
 * Le format est modifiable à tout moment, y compris en pleine année : le
 * garder pour toute la session laisserait un onglet ouvert travailler sur un
 * format périmé. Les écrans le rechargent donc à l'ouverture (`forcer`), la
 * mémorisation ne servant qu'à ne pas multiplier l'appel dans un même écran.
 */
let formatCharge = null

export function chargerFormatNumerotation (options) {
  if (formatCharge && !(options && options.forcer)) return formatCharge
  formatCharge = lireParametres().then(function (parametres) {
    return definirFormatNumerotation(parametres.numerotation || null)
  }, function () {
    return definirFormatNumerotation(null)
  })
  return formatCharge
}

/** À appeler après avoir enregistré les paramètres : le format a pu changer. */
export function oublierFormatNumerotation () {
  formatCharge = null
}

/** Enregistre les paramètres de facturation. Réservé à l'administration (403 sinon). */
export function majParametres (parametres) {
  return Vue.http.put(base() + '/parametres', parametres).then(function (reponse) {
    oublierFormatNumerotation()
    return reponse.data || {}
  })
}

export default {
  depotDisponible,
  depotLocal,
  listerFactures,
  enregistrerFactures,
  majFacture,
  supprimerFacture,
  envoyerFactures,
  lireEnvois,
  lireParametres,
  chargerFormatNumerotation,
  oublierFormatNumerotation,
  majParametres,
  renumeroterFacture,
  renumeroterLot
}



// WEBPACK FOOTER //
// ./src/utils/facture/depot.js